import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  notificationService,
  type NotificationSettings,
  urlBase64ToUint8Array,
} from '@/services/notificationService';

const DEFAULT_SETTINGS: NotificationSettings = {
  enabled: true,
  sendTime: '09:00',
  lastYear: true,
  pastToday: true,
  lastMonth: true,
  photoDay: true,
  badgeEnabled: true,
  pushEnabled: false,
};

export default function NotificationSettingsPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [vapidKey, setVapidKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [registeringPush, setRegisteringPush] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    notificationService.getSettings()
      .then((result) => {
        setSettings(result.settings);
        setVapidKey(result.vapidPublicKey);
      })
      .catch(() => setMessage('알림 설정을 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  }, []);

  const save = async (next = settings) => {
    setSaving(true);
    setMessage('');
    try {
      const result = await notificationService.saveSettings(next);
      setSettings(result.settings);
      setMessage('알림 설정을 저장했습니다.');
      return true;
    } catch {
      setMessage('알림 설정을 저장하지 못했습니다.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const enablePush = async () => {
    if (registeringPush || loading || saving) return;
    setRegisteringPush(true);
    setMessage('');
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window) || !vapidKey) {
        throw new Error('unsupported');
      }
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setMessage('휴대폰 설정에서 Family Album 알림을 허용해주세요.');
        return;
      }
      let readyTimer: ReturnType<typeof setTimeout> | undefined;
      const registration = await Promise.race([
        navigator.serviceWorker.register('/sw.js').then(() => navigator.serviceWorker.ready),
        new Promise<never>((_, reject) => {
          readyTimer = setTimeout(() => reject(new Error('service-worker-timeout')), 10000);
        }),
      ]).finally(() => { if (readyTimer !== undefined) clearTimeout(readyTimer); });
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }
      await notificationService.subscribe(subscription.toJSON());
      const next = { ...settings, enabled: true, pushEnabled: true };
      if (await save(next)) setMessage('휴대폰 알림과 앱 아이콘 배지를 켰습니다.');
    } catch {
      setMessage('알림 등록에 실패했습니다. 홈 화면에 설치한 앱에서 다시 시도해주세요.');
    } finally {
      setRegisteringPush(false);
    }
  };

  const preview = async () => {
    setMessage('오늘의 추억을 찾는 중...');
    try {
      const result = await notificationService.createPreview();
      if (result.notification) {
        setMessage('오늘의 추억 알림을 만들었습니다. 받은 알림에서 확인하세요.');
      }
    } catch {
      setMessage('오늘 조건에 맞는 추억 사진이 없습니다.');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 pb-28">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-background-200 bg-background-50/95 px-4 py-4 backdrop-blur">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary-100"
            aria-label="뒤로 가기"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-foreground-950">알림 설정</h1>
            <p className="text-xs text-foreground-400">시간과 알림 종류를 정합니다</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => save()}
          disabled={saving || loading}
          className="text-sm font-semibold text-accent-600 disabled:opacity-40"
        >
          {saving ? '저장 중' : '저장'}
        </button>
      </header>

      <main className="space-y-5 p-4">
        <section className="space-y-5 rounded-3xl border border-background-200 bg-white p-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-foreground-900">추억 알림</h2>
              <p className="mt-1 text-sm text-foreground-500">하루에 랜덤 카테고리 하나만 보내드려요.</p>
            </div>
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(event) => setSettings({ ...settings, enabled: event.target.checked })}
              className="h-5 w-5"
              aria-label="추억 알림 사용"
            />
          </div>

          <label className="block">
            <span className="text-sm font-semibold text-foreground-900">발송 시간</span>
            <div className="mt-2 flex w-full min-w-0 rounded-2xl border border-background-200 px-4 py-3">
              <input
                type="time"
                value={settings.sendTime}
                onChange={(event) => setSettings({ ...settings, sendTime: event.target.value })}
                className="block w-full min-w-0 border-0 bg-transparent p-0 text-base outline-none"
              />
            </div>
          </label>

          <div className="grid grid-cols-2 gap-3">
            {([
              ['lastYear', '작년 오늘'],
              ['pastToday', '과거의 오늘'],
              ['lastMonth', '지난달 오늘'],
              ['photoDay', '사진이 있는 날'],
            ] as const).map(([key, label]) => (
              <label
                key={key}
                className="flex items-center gap-2 rounded-2xl border border-background-200 p-3 text-sm"
              >
                <input
                  type="checkbox"
                  checked={settings[key]}
                  onChange={(event) => setSettings({ ...settings, [key]: event.target.checked })}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>

          <label className="flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground-900">앱 아이콘 배지</span>
            <input
              type="checkbox"
              checked={settings.badgeEnabled}
              onChange={(event) => setSettings({ ...settings, badgeEnabled: event.target.checked })}
            />
          </label>
        </section>

        <section className="space-y-3 rounded-3xl border border-background-200 bg-white p-5">
          <h2 className="font-bold text-foreground-900">휴대폰 알림</h2>
          <p className="text-sm leading-relaxed text-foreground-500">
            홈 화면에 설치한 Family Album 앱에서 허용하면 잠금 화면과 앱 아이콘으로 알려드려요.
          </p>
          <button
            type="button"
            onClick={enablePush}
            disabled={loading || saving || registeringPush}
            className="w-full rounded-2xl bg-foreground-900 py-3.5 font-semibold text-white"
          >
            {registeringPush ? '알림 등록 중…' : '휴대폰 알림 허용'}
          </button>
          <button
            type="button"
            onClick={preview}
            className="w-full rounded-2xl border border-background-300 py-3.5 font-semibold text-foreground-900"
          >
            오늘의 추억 미리보기
          </button>
          <button
            type="button"
            onClick={() => navigate('/notifications')}
            className="w-full rounded-2xl bg-secondary-100 py-3.5 font-semibold text-foreground-700"
          >
            받은 알림 보기
          </button>
        </section>

        {message && (
          <p className="rounded-2xl bg-accent-50 px-4 py-3 text-center text-sm text-accent-700">
            {message}
          </p>
        )}
      </main>
    </div>
  );
}
